import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  addTerminalTools,
  createTerminal,
  getAuthUserSync,
  getMyTerminals,
  loadAuthUser,
  startGoogleLogin,
} from "../lib/api";
import { track } from "../lib/track";

// Adds a tool to the user's terminal (active stack, or a new one if they have
// none). Logged-out clicks send the user to the login page and return here.
export default function AddToTerminalButton({ slug, title, testid = "add-to-terminal" }) {
  const [user, setUser] = useState(getAuthUserSync() || null);
  const [busy, setBusy] = useState(false);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    let alive = true;
    loadAuthUser()
      .then((u) => alive && setUser(u))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const onClick = async () => {
    if (!user) {
      // Send to login, then come back to this tool page.
      startGoogleLogin(window.location.pathname + window.location.search);
      return;
    }
    if (busy || added) return;
    if (!slug) {
      toast.error("this tool can't be added");
      return;
    }
    setBusy(true);
    try {
      const stacks = (await getMyTerminals()) || [];
      const id = resolveActiveId(stacks);
      if (!id) {
        await createTerminal({ slugs: [slug] });
        track("terminal_create", { slug, surface: "tool" });
        toast.success(`added ${title} to your new terminal`);
      } else {
        await addTerminalTools(id, [slug]);
        track("terminal_add", { slug, surface: "tool" });
        const st = stacks.find((s) => s.id === id);
        toast.success(`added ${title} → ${st ? stackName(st) : "your terminal"}`);
      }
      setAdded(true);
      window.dispatchEvent(new CustomEvent("tv:stacks-changed"));
    } catch {
      toast.error("could not add to your terminal");
    } finally {
      setBusy(false);
    }
  };

  const label = !user
    ? "[＋] add to my terminal"
    : busy
    ? "adding…"
    : added
    ? "[✓] in your terminal"
    : "[＋] add to my terminal";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy || added}
      className={`tv-btn ${added ? "bg-black text-white" : "tv-btn-primary"} disabled:opacity-100`}
      title={user ? "Add this tool to your terminal" : "Log in to add this tool to your terminal"}
      data-testid={testid}
    >
      {label}
    </button>
  );
}

function resolveActiveId(stacks) {
  let id = null;
  try {
    id = localStorage.getItem("tv_active_terminal_id");
  } catch (e) {
    /* ignore */
  }
  if (id && stacks.some((s) => s.id === id)) return id;
  return stacks[0] ? stacks[0].id : null;
}

function stackName(s) {
  const t = s.title || "terminal";
  if (s.asset_class && t.endsWith(` · ${s.asset_class}`)) {
    return t.slice(0, -(` · ${s.asset_class}`.length));
  }
  return t;
}
